"use client";

import {
  DEFAULT_ADULTS,
  AGE_OPTIONS as DEFAULT_AGE_OPTIONS,
} from "@/constants";
import useSearchFormStore, {
  FilterRoomModalStateEnum,
} from "@/store/searchFormStore/searchFormStore";
import { generateListAge } from "@/utils";
import { XIcon } from "lucide-react";
import React, { useEffect, useRef } from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  Control,
  UseFormWatch,
} from "react-hook-form";
import CounterInput from "../CounterInput";
import {
  AgesConfigurations,
  RoomsConfigurations,
  SearchAvailableRoom,
} from "./SearchAvailableRoomMobile";
import {
  CONFIG_SEARCH_AVAILABLE_ROOM,
  deepCloneRoom,
  TChildren,
  TRoom,
} from "./ultils";
import { Switch } from "@/components/atoms/switch";
import { CustomSelect } from "@/components/atoms/dropdown";
import { Button } from "@/components/atoms/button";

interface RoomSelectProps {
  roomConfiguration?: RoomsConfigurations;
  AgesConfigurations?: AgesConfigurations;
  isStoreForm?: boolean;
  isTriggerFetchRoom?: boolean;
  isCustomTemplate?: boolean;
  initial_adult_count?: string;
  design?:string;
}

// Helper: Update filter room modal state if needed
const updateFilterRoomModal = (
  isTriggerFetchRoom: boolean,
  setFilterRoomModalState: (state: FilterRoomModalStateEnum) => void,
): void => {
  if (isTriggerFetchRoom) {
    setFilterRoomModalState(FilterRoomModalStateEnum.SUBMITED);
  }
};

// Helper: Sync children across multiple rooms
const syncChildrenAcrossRooms = (
  rooms: TRoom[],
  firstRoomAdults: number,
  firstRoomChildren: TChildren[],
): TRoom[] => {
  return rooms.map((room, index) => {
    if (index === 0) return room;

    const syncedChildren = (firstRoomChildren ?? []).map((child) => ({
      age: child?.age ? { ...child.age } : undefined,
      isBed: !!child?.isBed,
    }));

    return {
      ...room,
      adults: firstRoomAdults,
      children: syncedChildren,
    };
  });
};

// Helper: Create new children list for cloned room
const createChildrenListForClone = (clonedRoomData: TRoom): TChildren[] => {
  let newListOfChildren: TChildren[] = [];
  if (clonedRoomData.children && Array.isArray(clonedRoomData.children)) {
    newListOfChildren = clonedRoomData.children.map((child) => {
      const mutableAge = child?.age
        ? { label: child.age.label, value: child.age.value }
        : undefined;
      return {
        ...child,
        age: mutableAge,
        isBed: child?.isBed || false,
      };
    });
  }
  return newListOfChildren;
};

// Helper: Sync child age across rooms
const syncChildAgeAcrossRooms = (
  updatedRooms: TRoom[],
  childIndexToSync: number,
): TRoom[] => {
  if (
    updatedRooms.length > 1 &&
    updatedRooms[0]?.children?.[childIndexToSync]
  ) {
    const sourceChildAge = updatedRooms[0].children[childIndexToSync].age;

    for (let i = 1; i < updatedRooms.length; i++) {
      const room = updatedRooms[i];
      if (room?.children[childIndexToSync]) {
        const childToUpdate = room.children[childIndexToSync];
        childToUpdate.age = sourceChildAge ? { ...sourceChildAge } : undefined;
      }
    }
  }
  return updatedRooms;
};

// Helper: Sync child bed status across rooms
const syncChildBedStatusAcrossRooms = (
  updatedRooms: TRoom[],
  childIndexToSync: number,
): TRoom[] => {
  if (
    updatedRooms.length > 1 &&
    updatedRooms[0]?.children?.[childIndexToSync]
  ) {
    const sourceChildIsBed = updatedRooms[0].children[childIndexToSync].isBed;

    for (let i = 1; i < updatedRooms.length; i++) {
      const room = updatedRooms[i];
      if (room?.children[childIndexToSync]) {
        const childToUpdate = room.children[childIndexToSync];
        childToUpdate.isBed = sourceChildIsBed;
      }
    }
  }
  return updatedRooms;
};

// Stable key generator for child objects (avoids using array index)
const childKeyMap = new WeakMap<object, string>();
let childKeyCounter = 0;
function getChildKey(child: TChildren, roomIndex: number, childIndex: number) {
  if (!child || typeof child !== "object") {
    return `child-undefined-${roomIndex}-${childIndex}`;
  }
  if (!childKeyMap.has(child as object)) {
    childKeyMap.set(child as object, `child-${++childKeyCounter}`);
  }
  return childKeyMap.get(child as object)!;
}

// Top-level: renders a single child row (age select + bed toggle)
const ChildRow: React.FC<{
  roomIndex: number;
  childIndex: number;
  child: TChildren;
  roomConfiguration?: RoomsConfigurations;
  AgesConfigurations?: AgesConfigurations;
  ageValuePlaceholder: string;
  control: Control<SearchAvailableRoom>;
  SelectAge: (age: { label: string; value: number }, fieldName: string) => void;
  updateChildBed: (isBed: boolean, fieldName: string) => void;
  invalidAgeErrorMessage: string;
  design?:string;
}> = ({
  roomIndex,
  childIndex,
  child,
  roomConfiguration,
  AgesConfigurations,
  ageValuePlaceholder,
  control,
  SelectAge,
  updateChildBed,
  invalidAgeErrorMessage,
  design
}) => {

  const isCriDesign= design === "cri";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between gap-4">
        <p>
          {roomConfiguration?.childrenLabel} {childIndex + 1}
        </p>

        <div className="flex gap-2">
          <Controller
            control={control}
            name={`rooms.${roomIndex}.children.${childIndex}.age`}
            render={({ field, formState }) => (
              <CustomSelect
                isDisabled={roomIndex !== 0}
                value={field.value}
                error={
                  formState.errors?.rooms?.[roomIndex]?.children?.[childIndex]
                    ?.age?.message
                }
                labelClassName="text-[var(--text-gray-normal)] text-sm leading-5 font-medium"
                options={generateListAge(
                  Number(roomConfiguration?.maximumChildAge),
                  ageValuePlaceholder,
                )}
                onChange={(value) => {
                  field.onChange(value);
                  SelectAge(
                    value as { label: string; value: number },
                    field.name,
                  );
                }}
                hasError={child?.age?.value === -1}
                maxMenuHeight={120}
                placeholder={AgesConfigurations?.agePlaceholder || ""}
                containerClassName="w-[100px]"
                label={AgesConfigurations?.agesLabel}
                size="sm"
                styles={{
                  input: (base, _state) => ({ ...base, fontSize: "16px" }),
                }}
              />
            )}
          />

          <div className="flex flex-col items-center gap-1">
            <p className="text-sm leading-5 font-medium text-[var(--text-gray-normal)]">
              {roomConfiguration?.bedLabel}
            </p>
            <Controller
              control={control}
              name={`rooms.${roomIndex}.children.${childIndex}.isBed`}
              render={({ field }) => (
                <>
                  <Switch
                    onCheckedChange={(value) => {
                      field.onChange(value);
                      updateChildBed(value, field.name);
                    }}
                    checked={field.value}
                    className={`${isCriDesign ? "text-[#223B1D]":""}`}
                    disabled={roomIndex !== 0}
                  />
                  <p className={`text-xs leading-5 font-medium ${isCriDesign ? "text-[#223B1D]" : "text-[var(--text-gray-light)]"}`}>
                    {field.value
                      ? roomConfiguration?.withBedLabel
                      : roomConfiguration?.withoutBedLabel}
                  </p>
                </>
              )}
            />
          </div>
        </div>
      </div>
      {child?.age?.value === -1 && (
        <div className="text-xs text-red-600">{invalidAgeErrorMessage}</div>
      )}
    </div>
  );
};

// Top-level: renders a room card (header, counters, child rows)
const RoomCard: React.FC<{
  isCustomTemplate?: boolean;
  roomIndex: number;
  roomsLength: number;
  roomConfiguration?: RoomsConfigurations;
  control: Control<SearchAvailableRoom>;
  watch: UseFormWatch<SearchAvailableRoom>;
  handleRemoveRoom: (index: number) => void;
  incrementAdults: () => void;
  decrementAdults: () => void;
  incrementChildren: () => void;
  decrementChildren: () => void;
  SelectAge: (age: { label: string; value: number }, fieldName: string) => void;
  updateChildBed: (isBed: boolean, fieldName: string) => void;
  ageValuePlaceholder: string;
  AgesConfigurations?: AgesConfigurations;
  invalidAgeErrorMessage: string;
  design?:string;
}> = ({
  isCustomTemplate,
  roomIndex,
  roomsLength,
  roomConfiguration,
  control,
  watch,
  handleRemoveRoom,
  incrementAdults,
  decrementAdults,
  incrementChildren,
  decrementChildren,
  SelectAge,
  updateChildBed,
  ageValuePlaceholder,
  AgesConfigurations,
  invalidAgeErrorMessage,
  design
}) => {
  const children = watch(`rooms.${roomIndex}.children`) as TChildren[];

  const isCriDesign= design === "cri";

  return (
    <div className="rounded-md border border-[var(--stroke-gray-light)]">
      <div className="flex items-center justify-between border-b border-b-[var(--stroke-gray-light)] p-3">
        <h2
          className={`font-medium ${isCustomTemplate ? isCriDesign ?"text-[#223B1D]" : "text-[var(--text-base-primary)]" : "text-[var(--text-gray-normal)]"}`}
        >
          {roomConfiguration?.singleRoomLabel} {roomIndex + 1}
        </h2>
        {roomsLength > 1 && roomIndex !== 0 && (
          <button
            className="cursor-pointer"
            type="button"
            onClick={() => handleRemoveRoom(roomIndex)}
          >
            <XIcon
              width={16}
              height={16}
              color={
                isCustomTemplate
                  ? "var(--text-base-primary)"
                  : "var(--text-gray-light)"
              }
            />
          </button>
        )}
      </div>
      
      <div className="px-3 pt-2 pb-3">
        <div className="flex gap-4">
          <div className="flex-1 space-y-1">
            <Controller
              name={`rooms.${roomIndex}.adults`}
              control={control}
              render={({ field }) => (
                <CounterInput
                  isCustomTemplate={isCustomTemplate}
                  design={design}
                  label={roomConfiguration?.adultLabel || ""}
                  value={field.value ?? 0}
                  disabled={roomIndex !== 0}
                  onIncrement={incrementAdults}
                  onDecrement={decrementAdults}
                  minValue={1}
                  maxValue={
                    roomConfiguration?.maximumAdults
                      ? Number(roomConfiguration.maximumAdults)
                      : undefined
                  }
                />
              )}
            />
          </div>

          <div className="flex-1 space-y-1">
            <Controller
              name={`rooms.${roomIndex}.children`}
              control={control}
              render={({ field }) => (
                <CounterInput
                  isCustomTemplate={isCustomTemplate}
                  design={design}
                  label={roomConfiguration?.childrenLabel || ""}
                  labelClassName="h-5"
                  value={field?.value?.length ?? 0}
                  disabled={roomIndex !== 0}
                  onDecrement={decrementChildren}
                  maxValue={
                    roomConfiguration?.maximumChildren
                      ? Number(roomConfiguration.maximumChildren)
                      : undefined
                  }
                  onIncrement={incrementChildren}
                />
              )}
            />
          </div>
        </div>

        {children?.length > 0 && (
          <div className="mt-4 flex flex-col gap-4">
            {children?.map((childItem: TChildren, childIndex: number) => (
              <ChildRow
                key={getChildKey(childItem, roomIndex, childIndex)}
                roomIndex={roomIndex}
                childIndex={childIndex}
                child={childItem}
                roomConfiguration={roomConfiguration}
                AgesConfigurations={AgesConfigurations}
                ageValuePlaceholder={ageValuePlaceholder}
                control={control}
                SelectAge={SelectAge}
                updateChildBed={updateChildBed}
                invalidAgeErrorMessage={invalidAgeErrorMessage}
                design={design}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const RoomSelect: React.FC<RoomSelectProps> = ({
  roomConfiguration,
  AgesConfigurations,
  isStoreForm = true,
  isTriggerFetchRoom = true,
  isCustomTemplate = false,
  initial_adult_count,
  design
}) => {
  const { control, watch, setValue } = useFormContext<SearchAvailableRoom>();
  const {
    fields: rooms,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "rooms",
  });
  const firstRoom = watch("rooms.0");
  const { setFormData, formData, setFilterRoomModalState } =
    useSearchFormStore();
  const { maximumAdults, maximumChildren } = roomConfiguration ?? {};

  const MAXIMUM_ADULTS_LIMIT = maximumAdults
    ? Number(maximumAdults)
    : CONFIG_SEARCH_AVAILABLE_ROOM.MAXIMUM_ADULTS_LIMIT;
  const MAXIMUM_CHILDREN_LIMIT = maximumChildren
    ? Number(maximumChildren)
    : CONFIG_SEARCH_AVAILABLE_ROOM.MAXIMUM_CHILDREN_LIMIT;
  const MAXIMUM_ROOMS_LIMIT = roomConfiguration?.maximumRooms
    ? Number(roomConfiguration.maximumRooms)
    : CONFIG_SEARCH_AVAILABLE_ROOM.MAXIMUM_ROOMS_LIMIT;
  const firstRoomAdults =
    formData?.rooms?.[0]?.adults ||
    Number(initial_adult_count) ||
    Number(roomConfiguration?.default_adult_count) ||
    Number(DEFAULT_ADULTS);

  const firstRoomChildren = watch("rooms.0.children") ?? [];
  const firstRoomChildrenLength = firstRoomChildren?.length ?? 0;
  const canIncreaseAdults = firstRoomAdults < MAXIMUM_ADULTS_LIMIT;
  const canDecreaseAdults = firstRoomAdults > 1;
  const canIncreaseChildren = firstRoomChildrenLength < MAXIMUM_CHILDREN_LIMIT;
  const canDecreaseChildren = firstRoomChildrenLength > 0;
  const invalidAgeErrorMessage =
    AgesConfigurations?.invalidAgeError || "お子様の年齢を選択してください";
  const ageValuePlaceholder =
    AgesConfigurations?.ageValuePlaceholder || "年齢を選択してください";
  const AGE_OPTIONS =
    generateListAge(
      Number(roomConfiguration?.maximumChildAge),
      ageValuePlaceholder,
    ) || DEFAULT_AGE_OPTIONS;
  const default_adults =
    Number(roomConfiguration?.default_adult_count) ||
    Number(DEFAULT_ADULTS) ||
    1;
  const isInitializedRef = useRef(false);
  const lastSyncedAdultsRef = useRef<number>(firstRoomAdults);
  const lastSyncedChildrenLengthRef = useRef<number>(firstRoomChildrenLength);
  const isAdultCountAppliedRef = useRef(false);

  // Apply default_adult_count / initial_adult_count to the form on first load.
  // The form defaultValues use DEFAULT_ADULTS=1; this corrects it to the
  // hotel-specific value when no persisted formData is available.
  useEffect(() => {
    if (isAdultCountAppliedRef.current) return;
    if (formData?.rooms?.[0]?.adults) return; // store already has a value

    const targetAdults =
      Number(initial_adult_count) ||
      Number(roomConfiguration?.default_adult_count);
    if (!targetAdults || targetAdults <= 0) return;

    const currentRooms = watch("rooms") as TRoom[] | undefined;
    if (!currentRooms || currentRooms.length === 0) return;

    const newRooms = deepCloneRoom(currentRooms);
    if (newRooms[0]) {
      newRooms[0].adults = targetAdults;
      setValue("rooms", newRooms);
    }
    isAdultCountAppliedRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomConfiguration?.default_adult_count, initial_adult_count]);

  // Initialize default room if none exists
  useEffect(() => {
    if (!firstRoom && !isInitializedRef.current) {
      const defaultRoom: TRoom = {
        adults: default_adults,
        children: [],
      };
      setValue("rooms", [defaultRoom]);
      isInitializedRef.current = true;
    }
  }, [firstRoom, default_adults, setValue]);

  // Sync children across rooms when first room changes
  useEffect(() => {
    // Skip if not initialized or no rooms
    if (!firstRoom || rooms.length <= 1) {
      return;
    }

    // Only sync if adults or children count actually changed
    const adultsChanged = lastSyncedAdultsRef.current !== firstRoomAdults;
    const childrenLengthChanged =
      lastSyncedChildrenLengthRef.current !== firstRoomChildrenLength;

    if (adultsChanged || childrenLengthChanged) {
      lastSyncedAdultsRef.current = firstRoomAdults;
      lastSyncedChildrenLengthRef.current = firstRoomChildrenLength;

      const currentRooms = rooms as TRoom[];
      const updatedRooms = syncChildrenAcrossRooms(
        currentRooms,
        firstRoomAdults,
        firstRoomChildren,
      );

      setValue("rooms", updatedRooms, { shouldDirty: false });

      if (isStoreForm) {
        setFormData({
          ...(formData as SearchAvailableRoom),
          rooms: updatedRooms,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRoomAdults, firstRoomChildrenLength, firstRoom, rooms.length]);

  const handleAddRoom = () => {
    if (rooms.length >= MAXIMUM_ROOMS_LIMIT) {
      return;
    }
    const currentFirstRoomRHF = watch("rooms.0") as TRoom | undefined;
    let baseRoomToClone: TRoom;

    if (currentFirstRoomRHF) {
      baseRoomToClone = currentFirstRoomRHF;
    } else {
      baseRoomToClone = { adults: Number(DEFAULT_ADULTS), children: [] };
    }

    const clonedRoomData = deepCloneRoom([baseRoomToClone])[0];
    const newListOfChildren = createChildrenListForClone(clonedRoomData);

    const roomToAppend: TRoom = {
      ...clonedRoomData,
      children: newListOfChildren,
    };

    append(roomToAppend);

    if (isStoreForm) {
      const existingStoreRooms = Array.isArray(formData?.rooms)
        ? formData.rooms
        : [];
      const roomForStore = {
        ...clonedRoomData,
        children: newListOfChildren,
      };
      const updatedStoreRooms = [...existingStoreRooms, roomForStore];

      setFormData({
        ...(formData as SearchAvailableRoom),
        rooms: updatedStoreRooms,
      });
    }
    updateFilterRoomModal(isTriggerFetchRoom, setFilterRoomModalState);
  };

  const handleRemoveRoom = (index: number) => {
    if (index === 0 && rooms?.length > 1) return;
    if (rooms?.length === 1 && index === 0) return;

    remove(index);
    if (isStoreForm && formData?.rooms) {
      const data = formData?.rooms?.filter((_, i) => i != index);
      setFormData({ ...formData, rooms: data });
    }
    if (isTriggerFetchRoom)
      setFilterRoomModalState(FilterRoomModalStateEnum.SUBMITED);
  };

  const incrementAdults = () => {
    if (rooms?.[0] && canIncreaseAdults) {
      const newRooms = deepCloneRoom([...(rooms as TRoom[])]);
      if (!newRooms[0]) newRooms[0] = { adults: 0, children: [] };
      newRooms[0] = { ...newRooms[0], adults: (newRooms[0].adults || 0) + 1 };
      setValue("rooms", newRooms);
      if (isStoreForm) {
        setFormData({ ...(formData as SearchAvailableRoom), rooms: newRooms });
      }
      updateFilterRoomModal(isTriggerFetchRoom, setFilterRoomModalState);
    }
  };

  const decrementAdults = () => {
    if (rooms?.[0] && canDecreaseAdults) {
      const newRooms = deepCloneRoom([...(rooms as TRoom[])]);
      if (newRooms[0]) {
        newRooms[0].adults -= 1;
        newRooms[0] = {
          adults: newRooms[0].adults,
          children: newRooms[0].children,
        };
      }
      setValue("rooms", newRooms);
      if (isStoreForm) {
        setFormData({
          ...(formData as SearchAvailableRoom),
          rooms: newRooms,
        });
      }
      updateFilterRoomModal(isTriggerFetchRoom, setFilterRoomModalState);
    }
  };

  const incrementChildren = () => {
    if (rooms?.[0] && canIncreaseChildren) {
      const newRooms = deepCloneRoom([...(rooms as TRoom[])]);
      if (!newRooms[0]) newRooms[0] = { adults: default_adults, children: [] };
      if (!newRooms[0].children) newRooms[0].children = [];
      newRooms[0].children = [
        ...newRooms[0].children,
        {
          age: AGE_OPTIONS[0],
          isBed: false,
        },
      ];
      setValue("rooms", newRooms);
      if (isStoreForm) {
        setFormData({ ...(formData as SearchAvailableRoom), rooms: newRooms });
      }
      updateFilterRoomModal(isTriggerFetchRoom, setFilterRoomModalState);
    }
  };

  const decrementChildren = () => {
    if (rooms?.[0] && canDecreaseChildren) {
      const newRooms = deepCloneRoom([...(rooms as TRoom[])]);
      if (newRooms[0]?.children) {
        newRooms[0].children.pop();
      }
      setValue("rooms", newRooms);
      if (isStoreForm) {
        setFormData({ ...(formData as SearchAvailableRoom), rooms: newRooms });
      }
      updateFilterRoomModal(isTriggerFetchRoom, setFilterRoomModalState);
    }
  };

  const SelectAge = (
    age: { label: string; value: number },
    fieldName: string,
  ) => {
    const childrenField = fieldName?.split(".");
    const childIndexToSync = Number(childrenField[3]);

    const currentRoomsState = watch("rooms") as TRoom[] | undefined;
    if (!currentRoomsState) return;

    const updatedRooms = deepCloneRoom(currentRoomsState);
    const syncedRooms = syncChildAgeAcrossRooms(updatedRooms, childIndexToSync);

    setValue("rooms", syncedRooms, { shouldValidate: false });
    if (isStoreForm) {
      setFormData({ ...(formData as SearchAvailableRoom), rooms: syncedRooms });
    }
    updateFilterRoomModal(isTriggerFetchRoom, setFilterRoomModalState);
  };

  const updateChildBed = (isBed: boolean, fieldName: string) => {
    const childrenField = fieldName?.split(".");
    const childIndexToSync = Number(childrenField[3]);

    const currentRoomsState = watch("rooms") as TRoom[] | undefined;
    if (!currentRoomsState) return;

    const updatedRooms = deepCloneRoom(currentRoomsState);
    const syncedRooms = syncChildBedStatusAcrossRooms(
      updatedRooms,
      childIndexToSync,
    );

    setValue("rooms", syncedRooms, { shouldValidate: false });
    if (isStoreForm) {
      setFormData({ ...(formData as SearchAvailableRoom), rooms: syncedRooms });
    }
    updateFilterRoomModal(isTriggerFetchRoom, setFilterRoomModalState);
  };

  // Load form data from store on mount only
  useEffect(() => {
    if (isStoreForm && formData?.rooms && !isInitializedRef.current) {
      setValue("rooms", formData.rooms);
      isInitializedRef.current = true;
      lastSyncedAdultsRef.current = formData.rooms[0]?.adults ?? default_adults;
      lastSyncedChildrenLengthRef.current =
        formData.rooms[0]?.children?.length ?? 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div className="flex flex-col gap-4">
      {rooms.map((roomItem: TRoom, roomIndex: number) => (
        <RoomCard
          isCustomTemplate={isCustomTemplate}
          key={roomItem.id}
          roomIndex={roomIndex}
          roomsLength={rooms.length}
          roomConfiguration={roomConfiguration}
          control={control}
          watch={watch}
          handleRemoveRoom={handleRemoveRoom}
          incrementAdults={incrementAdults}
          decrementAdults={decrementAdults}
          incrementChildren={incrementChildren}
          decrementChildren={decrementChildren}
          SelectAge={SelectAge}
          updateChildBed={updateChildBed}
          ageValuePlaceholder={ageValuePlaceholder}
          AgesConfigurations={AgesConfigurations}
          invalidAgeErrorMessage={invalidAgeErrorMessage}
          design={design}
        />
      ))}
      {/* Add Room Button */}
      <Button
        type="button"
        className={`w-fit ${design === "cri"? "bg-[#EFE9D8] text-[#2D4A36]":""}`}
        onClick={handleAddRoom}
        hierarchy={"tonal"}
        size={"lg"}
        disabled={rooms.length >= MAXIMUM_ROOMS_LIMIT}
      >
        {roomConfiguration?.addRoomButtonText}
      </Button>
      {!isCustomTemplate && (
        <p className="text-sm text-[var(--text-gray-light)]">
          ※お部屋ごとの人数変更機能はご利用いただけません。人数が異なる場合は予約を分けてお申し込みください。
        </p>
      )}
    </div>
  );
};

export { RoomSelect };
